import React, { useState, useEffect } from "react";
import { generateClient, GraphQLResult } from "aws-amplify/data";
import {
  listE2eTestsQuery,
  IListE2eTestsResponse,
  IE2ETestCase,
  listTestReportsQuery,
  IListTestReportsResponse,
  ITestReport,
  getTestReportDownloadUrlQuery,
  IGetTestReportDownloadUrlResponse,
  triggerE2eTestMutation,
  ITriggerE2eTestResponse,
} from "../lib";

const styles = {
  container: { padding: "20px" },
  section: { marginBottom: "30px" },
  button: {
    backgroundColor: "black",
    color: "white",
    outline: "none",
    fontSize: 14,
    padding: "10px 16px",
    margin: "0 10px 10px 0",
    borderStyle: "none",
    cursor: "pointer",
  },
  disabledButton: {
    backgroundColor: "darkgray",
    color: "white",
    outline: "none",
    fontSize: 14,
    padding: "10px 16px",
    margin: "0 10px 10px 0",
    borderStyle: "none",
    cursor: "not-allowed",
  },
  select: {
    fontSize: 14,
    padding: "8px 12px",
    marginRight: "10px",
    minWidth: "250px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
    marginTop: "10px",
  },
  th: {
    textAlign: "left" as const,
    padding: "8px 12px",
    borderBottom: "2px solid #333",
    fontWeight: 600,
  },
  td: { padding: "8px 12px", borderBottom: "1px solid #ddd" },
  downloadBtn: {
    backgroundColor: "#2196F3",
    color: "white",
    border: "none",
    padding: "4px 10px",
    cursor: "pointer",
    fontSize: 12,
  },
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function getInclusiveDateRange(
  fromDate: string,
  toDate?: string,
  today = new Date(),
): string[] {
  const endDate = toDate || formatDate(today);
  const currentDate = new Date(`${fromDate}T00:00:00`);
  const finalDate = new Date(`${endDate}T00:00:00`);
  const days: string[] = [];

  while (currentDate <= finalDate) {
    days.push(formatDate(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return days;
}

export function isDateRangeValid(fromDate: string, toDate: string): boolean {
  return !fromDate || !toDate || fromDate <= toDate;
}

function getDaysAgo(daysAgo: number): string {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return formatDate(date);
}

const E2ETests: React.FC = () => {
  const [testCases, setTestCases] = useState<IE2ETestCase[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedTest, setSelectedTest] = useState<string>("");
  const [reports, setReports] = useState<ITestReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState(() => getDaysAgo(6));
  const [toDate, setToDate] = useState(() => formatDate(new Date()));
  const [selectedTestType, setSelectedTestType] = useState("");

  const fetchTestCases = async () => {
    try {
      const client = generateClient({ authMode: "identityPool" });
      const res = (await client.graphql({
        query: listE2eTestsQuery,
      })) as GraphQLResult<IListE2eTestsResponse>;

      if (res?.data?.listE2eTests) {
        const list = res.data.listE2eTests;
        setTestCases(list);
        if (list.length > 0 && !selectedCategory) {
          const firstCategory = list[0].category || "Uncategorized";
          setSelectedCategory(firstCategory);
          const firstInCategory = list.find(
            (t) => (t.category || "Uncategorized") === firstCategory,
          );
          if (firstInCategory) setSelectedTest(firstInCategory.name);
        }
      }
    } catch (error) {
      console.error("Error fetching test cases:", error);
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const client = generateClient({ authMode: "identityPool" });
      const res = (await client.graphql({
        query: listTestReportsQuery,
        variables: {
          prefix: "auto-test-results/",
          days: getInclusiveDateRange(fromDate, toDate),
          testType: selectedTestType || undefined,
        },
      })) as GraphQLResult<IListTestReportsResponse>;

      if (res?.data?.listTestReports) {
        setReports(res.data.listTestReports);
      }
    } catch (error) {
      console.error("Error fetching test reports:", error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const triggerTest = async () => {
    if (!selectedTest) return;
    setTriggerLoading(true);
    setMessage(null);
    try {
      const client = generateClient({ authMode: "identityPool" });
      const res = (await client.graphql({
        query: triggerE2eTestMutation,
        variables: { testName: selectedTest },
      })) as GraphQLResult<ITriggerE2eTestResponse>;

      const result = res?.data?.triggerE2eTest;
      if (result?.success) {
        setMessage(`${result.message}. Results will appear in 3-5 minutes.`);
      } else {
        setMessage(`Failed: ${result?.message || "Unknown error"}`);
      }
    } catch (error: any) {
      console.error("Error triggering test:", error);
      setMessage(`Error: ${error.message || "Failed to trigger test"}`);
    } finally {
      setTriggerLoading(false);
    }
  };

  const downloadReport = async (key: string) => {
    try {
      const client = generateClient({ authMode: "identityPool" });
      const res = (await client.graphql({
        query: getTestReportDownloadUrlQuery,
        variables: { key },
      })) as GraphQLResult<IGetTestReportDownloadUrlResponse>;

      const url = res?.data?.getTestReportDownloadUrl?.url;
      if (url) {
        window.open(url, "_blank");
      } else {
        alert("Failed to generate download URL");
      }
    } catch (error) {
      console.error("Error getting download URL:", error);
      alert("Error generating download URL");
    }
  };

  useEffect(() => {
    fetchTestCases();
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedTestCase = testCases.find((t) => t.name === selectedTest);

  const categories = Array.from(
    new Set(testCases.map((t) => t.category || "Uncategorized")),
  );
  const testsInCategory = testCases.filter(
    (t) => (t.category || "Uncategorized") === selectedCategory,
  );

  const handleCategoryChange = (next: string) => {
    setSelectedCategory(next);
    const first = testCases.find(
      (t) => (t.category || "Uncategorized") === next,
    );
    setSelectedTest(first?.name || "");
  };

  return (
    <div style={styles.container}>
      <div style={styles.section}>
        <h2>Run E2E Test</h2>
        <div
          style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
        >
          <select
            style={styles.select}
            value={selectedCategory}
            onChange={(e) => handleCategoryChange(e.target.value)}
            disabled={triggerLoading || testCases.length === 0}
          >
            {categories.length === 0 && (
              <option value="">Loading categories...</option>
            )}
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
          <select
            style={styles.select}
            value={selectedTest}
            onChange={(e) => setSelectedTest(e.target.value)}
            disabled={triggerLoading || testsInCategory.length === 0}
          >
            {testsInCategory.length === 0 && (
              <option value="">No tests in category</option>
            )}
            {testsInCategory.map((tc) => (
              <option key={tc.name} value={tc.name}>
                {tc.displayName}
              </option>
            ))}
          </select>
          <button
            style={
              triggerLoading || !selectedTest
                ? styles.disabledButton
                : styles.button
            }
            disabled={triggerLoading || !selectedTest}
            onClick={triggerTest}
          >
            {triggerLoading ? "Triggering..." : "Run Test"}
          </button>
        </div>
        {selectedTestCase && (
          <p style={{ color: "#666", fontSize: 13, marginTop: 4 }}>
            {selectedTestCase.description} (timeout:{" "}
            {selectedTestCase.timeoutMinutes} min)
          </p>
        )}
        {message && <p>{message}</p>}
      </div>

      <div style={styles.section}>
        <h2>
          Test Reports{" "}
          <button
            style={{ ...styles.button, fontSize: 12, padding: "4px 10px" }}
            onClick={fetchReports}
            disabled={loading}
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </h2>

        <div
          style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
        >
          <label style={{ margin: "0 10px 10px 0" }}>
            From{" "}
            <input
              type="date"
              value={fromDate}
              max={toDate || undefined}
              onChange={(e) => {
                const nextFromDate = e.target.value;
                if (isDateRangeValid(nextFromDate, toDate)) {
                  setFromDate(nextFromDate);
                }
              }}
              disabled={loading}
            />
          </label>
          <label style={{ margin: "0 10px 10px 0" }}>
            To{" "}
            <input
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e) => {
                const nextToDate = e.target.value;
                if (isDateRangeValid(fromDate, nextToDate)) {
                  setToDate(nextToDate);
                }
              }}
              disabled={loading}
            />
          </label>
          <label style={{ margin: "0 10px 10px 0" }}>
            Test type{" "}
            <select
              style={{ ...styles.select, minWidth: "180px" }}
              value={selectedTestType}
              onChange={(e) => setSelectedTestType(e.target.value)}
              disabled={loading}
            >
              <option value="">All test types</option>
              {testCases.map((testCase) => (
                <option key={testCase.name} value={testCase.name}>
                  {testCase.displayName}
                </option>
              ))}
            </select>
          </label>
        </div>

        {loading && reports.length === 0 && <p>Loading reports...</p>}

        {!loading && reports.length === 0 && <p>No test reports found.</p>}

        {reports.length > 0 && (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Test Name</th>
                <th style={styles.th}>Size</th>
                <th style={styles.th}>Last Modified</th>
                <th style={styles.th}>Download</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr key={report.key}>
                  <td style={styles.td}>{report.date}</td>
                  <td style={styles.td}>{report.testName}</td>
                  <td style={styles.td}>{formatFileSize(report.size)}</td>
                  <td style={styles.td}>
                    {report.lastModified
                      ? new Date(report.lastModified).toLocaleString()
                      : "-"}
                  </td>
                  <td style={styles.td}>
                    <button
                      style={styles.downloadBtn}
                      onClick={() => downloadReport(report.key)}
                    >
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default E2ETests;
